import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { KeyIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { profileApi } from '../services/api';
import { useDialog } from '../hooks/useDialog';

const ChangePasswordPage: React.FC = () => {
  const { t } = useTranslation(['auth', 'common']);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { alert } = useDialog();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const getPasswordStrength = (password: string): number => {
    if (!password) return 0;
    let strength = 0;
    if (password.length >= 6) strength++;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength++;
    return Math.min(strength, 4);
  };

  const strength = getPasswordStrength(formData.newPassword);
  const strengthColors = ['bg-gray-200', 'bg-red-400', 'bg-orange-400', 'bg-yellow-400', 'bg-green-400'];
  const strengthTexts = ['', t('auth:strengthWeak'), t('auth:strengthFair'), t('auth:strengthGood'), t('auth:strengthStrong')];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.newPassword !== formData.confirmPassword) {
      await alert(t('auth:passwordMismatch'), t('auth:passwordMismatch'), 'warning');
      return;
    }

    if (formData.newPassword.length < 6) {
      await alert(t('auth:passwordTooShort'), t('auth:passwordTooShort'), 'warning');
      return;
    }

    setLoading(true);
    try {
      await profileApi.changePassword({
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });
      await alert(t('auth:changePasswordSuccess'), t('auth:changePasswordSuccess'), 'success');
      setTimeout(() => navigate('/profile'), 1000);
    } catch (error: any) {
      await alert(error.response?.data?.message || t('auth:changePasswordFailed'), t('auth:changePasswordFailed'), 'danger');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto bg-bg-page min-h-full">
      <div className="bg-bg-surface rounded-lg shadow-sm border border-border-subtle p-6 mb-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 bg-success/20 rounded-full flex items-center justify-center">
            <KeyIcon className="w-8 h-8 text-success" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-50">{t('auth:changePassword')}</h1>
            <p className="text-gray-400">{t('auth:changePasswordSubtitle')}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{t('auth:currentPassword')}</label>
            <input
              type="password"
              value={formData.currentPassword}
              onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
              className="w-full px-3 py-2 border border-border-subtle rounded-lg focus:ring-2 focus:ring-success focus:border-transparent bg-bg-form text-gray-50"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{t('auth:newPassword')}</label>
            <input
              type="password"
              value={formData.newPassword}
              onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
              className="w-full px-3 py-2 border border-border-subtle rounded-lg focus:ring-2 focus:ring-success focus:border-transparent bg-bg-form text-gray-50"
              required
            />
            <p className="text-sm text-gray-400 mt-1">{t('auth:passwordHint')}</p>
          </div>

          {formData.newPassword && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">{t('auth:strengthLabel')}</label>
              <div className="flex gap-1">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className={`h-2 flex-1 rounded-full ${i <= strength ? strengthColors[strength] : 'bg-border-default'}`}
                  />
                ))}
              </div>
              <p className="text-sm text-gray-400">{strengthTexts[strength]}</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">{t('auth:confirmNewPassword')}</label>
            <input
              type="password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
              className="w-full px-3 py-2 border border-border-subtle rounded-lg focus:ring-2 focus:ring-success focus:border-transparent bg-bg-form text-gray-50"
              required
            />
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-4 py-2 border border-border-subtle rounded-lg hover:bg-bg-elevated text-gray-300 transition-colors cursor-pointer"
            >
              {t('common:cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-success hover:bg-success/80 text-white rounded-lg disabled:opacity-50 transition-colors cursor-pointer"
            >
              {loading ? t('auth:changing') : t('auth:changePassword')}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-info/10 border border-info/30 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <InformationCircleIcon className="w-5 h-5 text-info mt-0.5" />
          <div>
            <h3 className="text-sm font-medium text-gray-50 mb-2">{t('auth:securityTips')}</h3>
            <ul className="text-sm text-gray-300 space-y-1">
              <li>• {t('auth:securityTip1')}</li>
              <li>• {t('auth:securityTip2')}</li>
              <li>• {t('auth:securityTip3')}</li>
              <li>• {t('auth:securityTip4')}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
